import { Box, Divider, Paper, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../../componentes/notificationProvider";
import NewAlertModal from "../../../../componentes/newAlertModal";
import FlowHeader from "../components/flowHeader";
import FlowTable from "../components/flowTable";
import { useTabStyles } from "../components/styles";
import ModalBlocos from "./modalBlocos";
import MassBlocos from "./massBlocos";
import { blocoColumns } from "./columns";

import { Bloco, BlocoPayload } from "./types";
import api from "../../../../services/api";
import { AxiosError } from "axios";

export default function Blocos() {
  const [search, setSearch] = useState<string>("");
  const [blocos, setBlocos] = useState<Bloco[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isMassMode, setIsMassMode] = useState<boolean>(false);
  const [tableKey, setTableKey] = useState<number>(0);

  const [openBlocoModal, setOpenBlocoModal] = useState<boolean>(false);
  const [editingBloco, setEditingBloco] = useState<Bloco | null>(null);
  const [savingBloco, setSavingBloco] = useState<boolean>(false);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedBloco, setSelectedBloco] = useState<Bloco | null>(null);

  const theme = useTheme();
  const styles = useTabStyles(theme);
  const notify = useNotification();

  useEffect(() => {
    getBlocos();
  }, []);

  const getBlocos = async () => {
    try {
      setLoading(true);

      const response = await api.get("/easyFlow2.0/blocos");

      setBlocos(Array.isArray(response.data?.data) ? response.data.data : []);
      setTableKey((key) => key + 1);
    } catch (error: any) {
      console.error(error);
      notify(
        `Error:${
          error?.message || "Error: Não foi possível se conectar com o servidor"
        } `,
        "error",
        6000,
      );
    } finally {
      setLoading(false);
    }
  };

  const rowsForDisplay = useMemo(() => {
    if (!search.trim()) return blocos;

    const searchLower = search.toLowerCase();
    return blocos.filter((bloco) =>
      Object.values(bloco).some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(searchLower),
      ),
    );
  }, [blocos, search]);

  const handleOpenCreate = () => {
    setEditingBloco(null);
    setOpenBlocoModal(true);
  };

  const handleEditRow = (bloco: Bloco) => {
    setEditingBloco(bloco);
    setOpenBlocoModal(true);
  };

  const handleSaveBloco = async (payload: BlocoPayload, id?: number) => {
    try {
      setSavingBloco(true);

      if (id) {
        await api.put(`/easyFlow2.0/blocos/${id}`, {
          ...payload,
        });
        notify("Bloco atualizado com sucesso", "success");
      } else {
        await api.post(`/easyFlow2.0/blocos`, {
          ...payload,
        });
        notify("Bloco criado com sucesso", "success");
      }

      setOpenBlocoModal(false);
      setEditingBloco(null);
      await getBlocos();
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.error ||
          error?.message ||
          "Não foi possível salvar o bloco",
        "error",
      );
    } finally {
      setSavingBloco(false);
    }
  };

  const handleDeleteRow = (bloco: Bloco) => {
    setSelectedBloco(bloco);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBloco) return;
    try {
      await api.delete(`/easyFlow2.0/blocos/${selectedBloco.id}`);
      notify("Bloco removido com sucesso!", "success", 3000);
      await getBlocos();
    } catch (error) {
      if (error instanceof AxiosError)
        notify(
          error.response?.data?.error || "Erro ao remover bloco",
          "error",
          3000,
        );
      else notify("Não foi possível se conectar ao servidor!", "error", 3000);
    } finally {
      setOpenDeleteModal(false);
      setSelectedBloco(null);
    }
  };

  return (
    <Paper elevation={0} sx={styles.paper}>
      {isMassMode ? (
        <MassBlocos
          blocos={blocos}
          search={search}
          onSearchChange={setSearch}
          onBack={() => setIsMassMode(false)}
          onSaved={getBlocos}
        />
      ) : (
        <>
          <FlowHeader
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Pesquisar blocos"
            onEnterEditMode={() => setIsMassMode(true)}
            disableEditMode={loading}
            onAdd={handleOpenCreate}
            onRefresh={getBlocos}
            loading={loading}
          />
          <Divider
            flexItem
            sx={{ my: 1, borderColor: theme.palette.divider }}
          />
          <Box sx={styles.boxTable}>
            <FlowTable<Bloco>
              key={`blocos-${tableKey}`}
              id="blocosFlow"
              rows={rowsForDisplay}
              columns={blocoColumns}
              loadingTable={loading}
              onEditRow={handleEditRow}
              onDeleteRow={handleDeleteRow}
            />
          </Box>
        </>
      )}

      <ModalBlocos
        open={openBlocoModal}
        loading={savingBloco}
        blocoData={editingBloco}
        onClose={() => {
          setOpenBlocoModal(false);
          setEditingBloco(null);
        }}
        onSave={handleSaveBloco}
      />

      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        textContent={`Tem certeza que deseja excluir o bloco ${
          selectedBloco?.nome ?? ""
        }?`}
        handleButton={handleConfirmDelete}
        handleCancel={() => setSelectedBloco(null)}
        severity={"error"}
        singleButton={false}
      />
    </Paper>
  );
}
